export interface PaletteConfig {
  name: string;
  description: string;
  primary: string;
  secondary: string;
  accent: string;
  background: {
    dark: string;
    darker: string;
    card: string;
    hover: string;
  };
  text: {
    primary: string;
    secondary: string;
    muted: string;
    accent: string;
  };
  border: {
    default: string;
    hover: string;
    glow: string;
  };
  glow: string;
  glass: {
    background: string;
    backgroundLight: string;
    backgroundStrong: string;
    border: string;
    blur: {
      small: string;
      medium: string;
      large: string;
    };
  };
}

export const palettePresets: Record<string, PaletteConfig> = {
  cyan: {
    name: '青色科技',
    description: '经典青色科技风，适合数据展示和仪表盘',
    primary: '#00ffff',
    secondary: '#00bfff',
    accent: '#00ffff',
    background: {
      dark: '#0a0e27',
      darker: '#050811',
      card: '#0f1629',
      hover: '#1a2332',
    },
    text: {
      primary: '#ffffff',
      secondary: '#b8c5d6',
      muted: '#6b7a99',
      accent: '#00ffff',
    },
    border: {
      default: 'rgba(0, 255, 255, 0.2)',
      hover: 'rgba(0, 255, 255, 0.4)',
      glow: 'rgba(0, 255, 255, 0.6)',
    },
    glow: 'rgba(0, 255, 255, 0.3)',
    glass: {
      background: 'rgba(15, 22, 41, 0.6)',
      backgroundLight: 'rgba(15, 22, 41, 0.3)',
      backgroundStrong: 'rgba(15, 22, 41, 0.8)',
      border: 'rgba(0, 255, 255, 0.1)',
      blur: {
        small: '4px',
        medium: '10px',
        large: '20px',
      },
    },
  },
  purple: {
    name: '紫色幻境',
    description: '神秘紫色调，适合创意和艺术类应用',
    primary: '#8a2be2',
    secondary: '#9370db',
    accent: '#ba55d3',
    background: {
      dark: '#0f0a1a',
      darker: '#050308',
      card: '#1a0f2e',
      hover: '#251a3a',
    },
    text: {
      primary: '#ffffff',
      secondary: '#d8c5e8',
      muted: '#9d7fb8',
      accent: '#ba55d3',
    },
    border: {
      default: 'rgba(138, 43, 226, 0.2)',
      hover: 'rgba(138, 43, 226, 0.4)',
      glow: 'rgba(138, 43, 226, 0.6)',
    },
    glow: 'rgba(138, 43, 226, 0.3)',
    glass: {
      background: 'rgba(26, 15, 46, 0.6)',
      backgroundLight: 'rgba(26, 15, 46, 0.3)',
      backgroundStrong: 'rgba(26, 15, 46, 0.8)',
      border: 'rgba(138, 43, 226, 0.1)',
      blur: {
        small: '4px',
        medium: '10px',
        large: '20px',
      },
    },
  },
  pink: {
    name: '粉色霓虹',
    description: '活力粉色系，适合年轻化和时尚品牌',
    primary: '#ff1493',
    secondary: '#ff69b4',
    accent: '#ff1493',
    background: {
      dark: '#1a0a14',
      darker: '#0a0508',
      card: '#2a0f1e',
      hover: '#3a1a28',
    },
    text: {
      primary: '#ffffff',
      secondary: '#f5c2d9',
      muted: '#d18ba8',
      accent: '#ff1493',
    },
    border: {
      default: 'rgba(255, 20, 147, 0.2)',
      hover: 'rgba(255, 20, 147, 0.4)',
      glow: 'rgba(255, 20, 147, 0.6)',
    },
    glow: 'rgba(255, 20, 147, 0.3)',
    glass: {
      background: 'rgba(42, 15, 30, 0.6)',
      backgroundLight: 'rgba(42, 15, 30, 0.3)',
      backgroundStrong: 'rgba(42, 15, 30, 0.8)',
      border: 'rgba(255, 20, 147, 0.1)',
      blur: {
        small: '4px',
        medium: '10px',
        large: '20px',
      },
    },
  },
  green: {
    name: '绿色能量',
    description: '清新绿色调，适合环保和健康类应用',
    primary: '#00ff88',
    secondary: '#00d977',
    accent: '#00ff88',
    background: {
      dark: '#0a1a0f',
      darker: '#050a08',
      card: '#0f2a1a',
      hover: '#1a3a25',
    },
    text: {
      primary: '#ffffff',
      secondary: '#c2f5d9',
      muted: '#8bd1a8',
      accent: '#00ff88',
    },
    border: {
      default: 'rgba(0, 255, 136, 0.2)',
      hover: 'rgba(0, 255, 136, 0.4)',
      glow: 'rgba(0, 255, 136, 0.6)',
    },
    glow: 'rgba(0, 255, 136, 0.3)',
    glass: {
      background: 'rgba(15, 42, 26, 0.6)',
      backgroundLight: 'rgba(15, 42, 26, 0.3)',
      backgroundStrong: 'rgba(15, 42, 26, 0.8)',
      border: 'rgba(0, 255, 136, 0.1)',
      blur: {
        small: '4px',
        medium: '10px',
        large: '20px',
      },
    },
  },
  blue: {
    name: '蓝色深海',
    description: '深邃蓝色系，适合专业和商务场景',
    primary: '#00bfff',
    secondary: '#1e90ff',
    accent: '#00bfff',
    background: {
      dark: '#0a141f',
      darker: '#050a14',
      card: '#0f1a2a',
      hover: '#1a2535',
    },
    text: {
      primary: '#ffffff',
      secondary: '#c2e5f5',
      muted: '#8bb8d1',
      accent: '#00bfff',
    },
    border: {
      default: 'rgba(0, 191, 255, 0.2)',
      hover: 'rgba(0, 191, 255, 0.4)',
      glow: 'rgba(0, 191, 255, 0.6)',
    },
    glow: 'rgba(0, 191, 255, 0.3)',
    glass: {
      background: 'rgba(15, 26, 42, 0.6)',
      backgroundLight: 'rgba(15, 26, 42, 0.3)',
      backgroundStrong: 'rgba(15, 26, 42, 0.8)',
      border: 'rgba(0, 191, 255, 0.1)',
      blur: {
        small: '4px',
        medium: '10px',
        large: '20px',
      },
    },
  },
  orange: {
    name: '橙色烈焰',
    description: '温暖橙色系，适合活力和创意类应用',
    primary: '#ff6b35',
    secondary: '#ff8c42',
    accent: '#ff6b35',
    background: {
      dark: '#1a0f0a',
      darker: '#0a0503',
      card: '#2a1a0f',
      hover: '#3a251a',
    },
    text: {
      primary: '#ffffff',
      secondary: '#f5d4c2',
      muted: '#d1a88b',
      accent: '#ff6b35',
    },
    border: {
      default: 'rgba(255, 107, 53, 0.2)',
      hover: 'rgba(255, 107, 53, 0.4)',
      glow: 'rgba(255, 107, 53, 0.6)',
    },
    glow: 'rgba(255, 107, 53, 0.3)',
    glass: {
      background: 'rgba(42, 26, 15, 0.6)',
      backgroundLight: 'rgba(42, 26, 15, 0.3)',
      backgroundStrong: 'rgba(42, 26, 15, 0.8)',
      border: 'rgba(255, 107, 53, 0.1)',
      blur: {
        small: '4px',
        medium: '10px',
        large: '20px',
      },
    },
  },
  red: {
    name: '红色激情',
    description: '热烈红色系，适合运动和娱乐类应用',
    primary: '#ff1744',
    secondary: '#ff4569',
    accent: '#ff1744',
    background: {
      dark: '#1a0a0d',
      darker: '#0a0506',
      card: '#2a0f14',
      hover: '#3a1a1f',
    },
    text: {
      primary: '#ffffff',
      secondary: '#f5c2c9',
      muted: '#d18b96',
      accent: '#ff1744',
    },
    border: {
      default: 'rgba(255, 23, 68, 0.2)',
      hover: 'rgba(255, 23, 68, 0.4)',
      glow: 'rgba(255, 23, 68, 0.6)',
    },
    glow: 'rgba(255, 23, 68, 0.3)',
    glass: {
      background: 'rgba(42, 15, 20, 0.6)',
      backgroundLight: 'rgba(42, 15, 20, 0.3)',
      backgroundStrong: 'rgba(42, 15, 20, 0.8)',
      border: 'rgba(255, 23, 68, 0.1)',
      blur: {
        small: '4px',
        medium: '10px',
        large: '20px',
      },
    },
  },
  gold: {
    name: '金色奢华',
    description: '奢华金色系，适合高端和金融类应用',
    primary: '#ffd700',
    secondary: '#ffed4e',
    accent: '#ffd700',
    background: {
      dark: '#1a140a',
      darker: '#0a0805',
      card: '#2a1f0f',
      hover: '#3a2f1a',
    },
    text: {
      primary: '#ffffff',
      secondary: '#f5ecc2',
      muted: '#d1c88b',
      accent: '#ffd700',
    },
    border: {
      default: 'rgba(255, 215, 0, 0.2)',
      hover: 'rgba(255, 215, 0, 0.4)',
      glow: 'rgba(255, 215, 0, 0.6)',
    },
    glow: 'rgba(255, 215, 0, 0.3)',
    glass: {
      background: 'rgba(42, 31, 15, 0.6)',
      backgroundLight: 'rgba(42, 31, 15, 0.3)',
      backgroundStrong: 'rgba(42, 31, 15, 0.8)',
      border: 'rgba(255, 215, 0, 0.1)',
      blur: {
        small: '4px',
        medium: '10px',
        large: '20px',
      },
    },
  },
  cyberpunk: {
    name: '赛博朋克',
    description: '霓虹紫青渐变，适合游戏和科幻类应用',
    primary: '#ff00ff',
    secondary: '#00ffff',
    accent: '#ff00ff',
    background: {
      dark: '#0a0514',
      darker: '#05020a',
      card: '#0f0a1f',
      hover: '#1a1532',
    },
    text: {
      primary: '#ffffff',
      secondary: '#e0c2f5',
      muted: '#b88bd1',
      accent: '#ff00ff',
    },
    border: {
      default: 'rgba(255, 0, 255, 0.2)',
      hover: 'rgba(255, 0, 255, 0.4)',
      glow: 'rgba(255, 0, 255, 0.6)',
    },
    glow: 'rgba(255, 0, 255, 0.3)',
    glass: {
      background: 'rgba(15, 10, 31, 0.6)',
      backgroundLight: 'rgba(15, 10, 31, 0.3)',
      backgroundStrong: 'rgba(15, 10, 31, 0.8)',
      border: 'rgba(255, 0, 255, 0.1)',
      blur: {
        small: '4px',
        medium: '10px',
        large: '20px',
      },
    },
  },
  silver: {
    name: '银色极简',
    description: '金属银色系，适合极简和现代设计',
    primary: '#c0c0c0',
    secondary: '#e8e8e8',
    accent: '#c0c0c0',
    background: {
      dark: '#0f0f0f',
      darker: '#050505',
      card: '#1a1a1a',
      hover: '#252525',
    },
    text: {
      primary: '#ffffff',
      secondary: '#d0d0d0',
      muted: '#999999',
      accent: '#c0c0c0',
    },
    border: {
      default: 'rgba(192, 192, 192, 0.2)',
      hover: 'rgba(192, 192, 192, 0.4)',
      glow: 'rgba(192, 192, 192, 0.6)',
    },
    glow: 'rgba(192, 192, 192, 0.3)',
    glass: {
      background: 'rgba(26, 26, 26, 0.6)',
      backgroundLight: 'rgba(26, 26, 26, 0.3)',
      backgroundStrong: 'rgba(26, 26, 26, 0.8)',
      border: 'rgba(192, 192, 192, 0.1)',
      blur: {
        small: '4px',
        medium: '10px',
        large: '20px',
      },
    },
  },
  teal: {
    name: '青绿自然',
    description: '清新青绿色，适合自然和健康类应用',
    primary: '#20b2aa',
    secondary: '#48d1cc',
    accent: '#20b2aa',
    background: {
      dark: '#0a1414',
      darker: '#050a0a',
      card: '#0f1f1f',
      hover: '#1a2a2a',
    },
    text: {
      primary: '#ffffff',
      secondary: '#c2f5f0',
      muted: '#8bd1c8',
      accent: '#20b2aa',
    },
    border: {
      default: 'rgba(32, 178, 170, 0.2)',
      hover: 'rgba(32, 178, 170, 0.4)',
      glow: 'rgba(32, 178, 170, 0.6)',
    },
    glow: 'rgba(32, 178, 170, 0.3)',
    glass: {
      background: 'rgba(15, 31, 31, 0.6)',
      backgroundLight: 'rgba(15, 31, 31, 0.3)',
      backgroundStrong: 'rgba(15, 31, 31, 0.8)',
      border: 'rgba(32, 178, 170, 0.1)',
      blur: {
        small: '4px',
        medium: '10px',
        large: '20px',
      },
    },
  },
  amber: {
    name: '琥珀暖阳',
    description: '温暖琥珀色，适合温馨和社交类应用',
    primary: '#ffbf00',
    secondary: '#ffcc33',
    accent: '#ffbf00',
    background: {
      dark: '#1a140a',
      darker: '#0a0805',
      card: '#2a1f0f',
      hover: '#3a2f1a',
    },
    text: {
      primary: '#ffffff',
      secondary: '#f5e6c2',
      muted: '#d1c08b',
      accent: '#ffbf00',
    },
    border: {
      default: 'rgba(255, 191, 0, 0.2)',
      hover: 'rgba(255, 191, 0, 0.4)',
      glow: 'rgba(255, 191, 0, 0.6)',
    },
    glow: 'rgba(255, 191, 0, 0.3)',
    glass: {
      background: 'rgba(42, 31, 15, 0.6)',
      backgroundLight: 'rgba(42, 31, 15, 0.3)',
      backgroundStrong: 'rgba(42, 31, 15, 0.8)',
      border: 'rgba(255, 191, 0, 0.1)',
      blur: {
        small: '4px',
        medium: '10px',
        large: '20px',
      },
    },
  },
};
